import type { Question } from "@rmh/defs";
import { useEffect, useRef, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { RatingSelector } from "./RatingSelector";

interface PromContentProps {
  questions: Question[];
  responses: (number | undefined)[];
  startIndex: number; // At what does the scale rate selecotr start
  index: number;
  onSelect: (value: number) => void;
  isRatingReversed: boolean;
  setCurrentQuestionIndex: (number: number) => void;
}

export default function PromContent({
  questions,
  responses,
  startIndex,
  index,
  onSelect,
  isRatingReversed,
  setCurrentQuestionIndex,
}: PromContentProps) {
  const questionCardRef = useRef<HTMLDivElement>(null);
  const [transitionComplete, setTransitionComplete] = useState(true);

  const isSmall = useMediaQuery("(max-width:768px)");

  const scrollContainerWidth = isSmall ? 530 : 600;

  useEffect(() => {
    const handleTransitionEnd = () => {
      setTransitionComplete(true);
    };
    if (questionCardRef.current) {
      questionCardRef.current.addEventListener(
        "transitionend",
        handleTransitionEnd
      );
    }
    return () => {
      if (questionCardRef.current) {
        questionCardRef.current.removeEventListener(
          "transitionend",
          handleTransitionEnd
        );
      }
    };
  }, []);

  return (
    <Box
      className="relative flex max-h-full flex-1 flex-col justify-center overflow-visible xl:max-h-[800px]"
      sx={{
        width: `${scrollContainerWidth}px`,
      }}
    >
      <div
        className="absolute flex h-full max-h-[520px] w-full grow transition-all duration-[800ms] ease-in-out"
        ref={questionCardRef}
        style={{ transform: `translateX(-${index * scrollContainerWidth}px)` }}
      >
        {questions.map((q, i) => {
          const res = responses[i];

          const isCurrentQuestion = index === i;

          const firstQuestionWidth = index === i && i === 0 ? "w-full" : "";

          const handleOtherQuestionClick = (questionNumber: number) => {
            setCurrentQuestionIndex(questionNumber - 1);
          };

          return (
            <Box
              className={`flex flex-col px-4  ${firstQuestionWidth} ${
                isCurrentQuestion ? "" : "justify-center"
              }`}
              data-cy={`question-${i}`}
              key={`question-${i}`}
              sx={{
                minWidth: `${scrollContainerWidth}px`,
                width: `${scrollContainerWidth}px`,
              }}
            >
              <RatingSelector
                instructions={q.promInstructions as string}
                isCurrentQuestion={isCurrentQuestion}
                isNavigable={Boolean(i === 0 || responses[i - 1] !== undefined)}
                isRatingReversed={isRatingReversed}
                onOtherQuestionClick={handleOtherQuestionClick}
                onSelect={(value) => {
                  setTransitionComplete(false);
                  onSelect(value);
                }}
                questionNumber={i + 1}
                questionText={q.text}
                ratingLabels={q.scaleLabels ?? []}
                scaleEndLabel={q.scaleEndLabel}
                scaleStartLabel={q.scaleStartLabel}
                start={startIndex}
                transitionComplete={transitionComplete}
                value={res}
              />
            </Box>
          );
        })}
      </div>
    </Box>
  );
}
